import { useEffect, useState } from "react";
import { api, formatARS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Courts() {
  const [courts, setCourts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", sport: "futbol", price_ars: "", duration_minutes: 60 });

  const load = async () => {
    const { data } = await api.get("/courts");
    setCourts(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || !form.price_ars) return toast.error("Completá nombre y precio");
    try {
      await api.post("/courts", {
        name: form.name,
        sport: form.sport,
        price_ars: parseFloat(form.price_ars),
        duration_minutes: parseInt(form.duration_minutes),
      });
      toast.success("Cancha creada");
      setOpen(false);
      setForm({ name: "", sport: "futbol", price_ars: "", duration_minutes: 60 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar cancha?")) return;
    await api.delete(`/courts/${id}`);
    toast.success("Eliminada");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-2">// Tu inventario</div>
          <h1 className="font-heading text-5xl tracking-tight">CANCHAS</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-black uppercase tracking-widest font-bold" data-testid="add-court-btn">
              <Plus className="h-4 w-4 mr-2" /> Agregar cancha
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#141414] border-[#262626]">
            <DialogHeader>
              <DialogTitle className="font-heading text-3xl tracking-wider">NUEVA CANCHA</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="uppercase tracking-widest text-xs text-zinc-400">Nombre</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" placeholder="Cancha 1" data-testid="court-name-input" />
              </div>
              <div>
                <Label className="uppercase tracking-widest text-xs text-zinc-400">Deporte</Label>
                <Select value={form.sport} onValueChange={(v) => setForm({ ...form, sport: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#262626] mt-1" data-testid="court-sport-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141414] border-[#262626]">
                    <SelectItem value="futbol">Fútbol</SelectItem>
                    <SelectItem value="padel">Pádel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="uppercase tracking-widest text-xs text-zinc-400">Precio (ARS)</Label>
                  <Input type="number" value={form.price_ars} onChange={(e) => setForm({ ...form, price_ars: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" placeholder="15000" data-testid="court-price-input" />
                </div>
                <div>
                  <Label className="uppercase tracking-widest text-xs text-zinc-400">Duración (min)</Label>
                  <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="bg-[#0A0A0A] border-[#262626] mt-1" data-testid="court-duration-input" />
                </div>
              </div>
              <Button onClick={submit} className="w-full bg-emerald-500 hover:bg-emerald-600 text-black uppercase tracking-widest font-bold" data-testid="court-submit-btn">
                Crear cancha
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {courts.length === 0 ? (
        <div className="bg-[#141414] border border-[#262626] rounded-lg p-16 text-center text-zinc-500" data-testid="no-courts">
          <div className="uppercase tracking-widest text-xs">No tenés canchas todavía. ¡Agregá la primera!</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {courts.map((c) => (
            <div key={c.court_id} className="bg-[#141414] border border-[#262626] rounded-lg p-6 hover:border-emerald-500/40 transition" data-testid={`court-${c.court_id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="text-xs uppercase tracking-[0.2em] text-emerald-400">{c.sport === "futbol" ? "⚽ Fútbol" : "🎾 Pádel"}</div>
                <button onClick={() => remove(c.court_id)} className="text-zinc-500 hover:text-red-400" data-testid={`del-${c.court_id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="font-heading text-3xl mb-2">{c.name}</div>
              <div className="text-2xl text-emerald-400 font-bold">{formatARS(c.price_ars)}</div>
              <div className="text-xs text-zinc-500 mt-1">{c.duration_minutes} minutos</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
